<template>
  <EInstantAnswer
    v-if="isValid(translatedPair)"
    :sources="[]"
    :feedback-link="{
      ticketSubject: 'Search - Translation',
      widgetType: 'translation_rich'
    }"
    class="translation"
    :sr-title="$t('common.search.widgets.translation.title')"
    footer-vertical-align="start"
    trees
  >
    <template #context>
      {{ $t('web.widgets.translation.context', { language: translatedPair.language }) }}
    </template>
    <div class="translation__languages">
      <EPair
        v-if="isValid(originalPair)"
        v-bind="originalPair"
        class="translation__pair translation__pair--original"
      />
      <EPair
        v-bind="translatedPair"
        class="translation__pair translation__pair--translated"
      />
    </div>
    <template #footer-left>
      <EButton
        v-if="source"
        as="a"
        :href="source.url"
        variant="outline"
        size="s"
        data-test-id="translation-source"
        class="translation__source"
        target="_blank"
        @click="onSourceClick"
      >
        {{ $t('common.generic.openin', { target: source.title }) }}
      </EButton>
    </template>
  </EInstantAnswer>
</template>

<script>
import upperFirst from 'lodash/upperFirst';
import { mapState } from 'vuex';

import { trackImpression, trackClick, PAGE_SERP, TYPE_RICH } from '~~/search/common/js/client/analytics/rich-content';
import { localiseLanguage } from '~~/common/js/universal/intl';
import EInstantAnswer from '~/components/instant-answers/base';
import EButton from '~~/common/vue/components/button';
import EPair from './pair';

export const TRACK_ID = 'translation';

export default {
  components: {
    EInstantAnswer,
    EPair,
    EButton,
  },
  props: {
    originalText: {
      type: String,
      default: '',
    },
    originalLanguage: {
      type: String,
      default: '',
    },
    translatedText: {
      type: String,
      default: '',
    },
    translatedLanguage: {
      type: String,
      default: '',
    },
    source: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapState('global', ['locale']),
    isTranslatedValid() {
      return Object.values(this.translatedPair).every((item) => !!item);
    },
    originalPair() {
      return this.getLanguagePair('original');
    },
    translatedPair() {
      return this.getLanguagePair('translated');
    },
  },
  mounted() {
    trackImpression({
      name: TRACK_ID,
      page: PAGE_SERP,
      type: TYPE_RICH,
    });
  },
  methods: {
    isValid(pair) {
      return Object.values(pair).every((item) => !!item);
    },
    getLanguagePair(type) {
      const languageCode = this[`${type}Language`];
      return {
        text: this[`${type}Text`],
        // Safari 13 doesn't support Intl language names, so we fall back to the language code
        language: upperFirst(localiseLanguage(this.locale, languageCode)) || languageCode,
        languageCode,
      };
    },
    onSourceClick: trackClick({
      name: TRACK_ID,
      type: TYPE_RICH,
      target: 'source',
    }),
  },
};
</script>

<style lang="scss" scoped>
.translation__languages {
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @include mobile-l {
    flex-direction: row;
  }

  > * {
    flex-grow: 1;
    width: 100%;
  }

  > :not(:first-child) {
    margin-top: $space-s;

    @include mobile-l {
      margin-top: 0;
      margin-left: $space-s;
    }
  }
}

.translation__pair--original {
  color: var(--color-text-secondary);
}
</style>
